import clsx from 'clsx'
import {
  type ReactElement,
  type ReactNode,
  cloneElement,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

export interface TooltipProps {
  /** Tooltip body content. */
  content: ReactNode
  /** Single React element child that receives aria-describedby + listeners. */
  children: ReactElement
  /** Position relative to the trigger. Default 'top'. */
  side?: 'top' | 'bottom' | 'left' | 'right'
  /** Default delay before showing. */
  delay?: number
}

const sideClass: Record<NonNullable<TooltipProps['side']>, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-1',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-1',
  left: 'right-full top-1/2 -translate-y-1/2 mr-1',
  right: 'left-full top-1/2 -translate-y-1/2 ml-1',
}

export function Tooltip({ content, children, side = 'top', delay = 150 }: TooltipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  // Persist the timeout id across renders. The previous `let timer` was
  // re-initialised every render, so a quick mouseleave/mouseenter pair (or
  // any unrelated re-render between them) would leak the show timer and the
  // tooltip could pop open after the cursor had already left. useRef plus
  // an unmount cleanup also prevents "setState on unmounted component"
  // warnings if the trigger unmounts during the delay window.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onShow = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setOpen(true), delay)
  }
  const onHide = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setOpen(false)
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  // Inject aria-describedby + listeners onto the child element.
  const trigger = cloneElement(children, {
    'aria-describedby': open ? id : undefined,
    onMouseEnter: onShow,
    onMouseLeave: onHide,
    onFocus: onShow,
    onBlur: onHide,
  } as Record<string, unknown>)

  return (
    <span className="relative inline-flex">
      {trigger}
      {open ? (
        <span
          id={id}
          role="tooltip"
          className={clsx(
            'absolute z-40 whitespace-nowrap rounded-[var(--radius-sm)] border border-[var(--border)]',
            'bg-[var(--bg-3)] px-2 py-1 text-[12px] text-[var(--text)] shadow-md pointer-events-none',
            sideClass[side],
          )}
        >
          {content}
        </span>
      ) : null}
    </span>
  )
}
